import { useCallback, useContext, useMemo, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { toast } from "sonner";
import posthog from "@/lib/posthog";
import { trpc } from "@/trpc";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { PipelinePosition } from "@/contexts/PipelineContext";
import UserContext from "@/contexts/UserContext";
import { canMoveLeadToPipelinePosition } from "@/utils/permissions";
import PositionColumn, { Columna } from "./PositionColumn";
import ProspectoCard from "./ProspectoCard";
import StickyScrollRail from "./StickyScrollRail";
import {
  buildColumnQueryInput,
  headerColorForStage,
  MOVABLE_STAGES,
  type TableroCardLead,
  type TableroLead,
  type TableroQueryFilters,
} from "./types";

type Props = {
  merchantId: string;
  pipelineId: string;
  positions: PipelinePosition[];
  filters: TableroQueryFilters;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
  employeeNameById: Map<string, string>;
  anyFilter: boolean;
  onClearFilters: () => void;
};

const KanbanBoard = ({
  merchantId,
  pipelineId,
  positions,
  filters,
  onSelectLead,
  selectedLeadId,
  employeeNameById,
  anyFilter,
  onClearFilters,
}: Props) => {
  const utils = trpc.useUtils();
  const { email } = useContext(UserContext);
  const moveMutation = trpc.leads.movePipelinePosition.useMutation();

  // 5px activation distance keeps card clicks from being swallowed by drag
  // detection (Lovable's constraint).
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const positionMetaById = useMemo(() => {
    const map = new Map<string, { stage: string; displayName: string }>();
    for (const p of positions) map.set(p.id, { stage: p.stage, displayName: p.displayName });
    return map;
  }, [positions]);

  // Loaded counts per column, reported by each PositionColumn, so the board
  // can show a single global empty state instead of a row of empty columns.
  const [statusByPosition, setStatusByPosition] = useState<
    Map<string, { count: number; isLoading: boolean }>
  >(new Map());
  const handleLeadsChange = useCallback(
    (positionId: string, leads: TableroLead[], isLoading: boolean) => {
      setStatusByPosition((prev) => {
        const next = new Map(prev);
        next.set(positionId, { count: leads.length, isLoading });
        return next;
      });
    },
    []
  );

  const allEmpty =
    positions.length > 0 &&
    positions.every((p) => {
      const status = statusByPosition.get(p.id);
      return status && !status.isLoading && status.count === 0;
    });

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const leadId = String(active.id);
    const sourcePositionId = active.data.current?.pipelinePositionId as string | undefined;
    const overId = String(over.id);
    if (!overId.startsWith("position:")) return;
    const targetPositionId = overId.slice("position:".length);

    if (!sourcePositionId || sourcePositionId === targetPositionId) return;

    const sourceMeta = positionMetaById.get(sourcePositionId);
    const targetMeta = positionMetaById.get(targetPositionId);
    if (!sourceMeta || !targetMeta) return;

    // Nothing is ever dropped INTO a WhatsApp column — those report delivery.
    // Leaving one by hand is allowed for the single case where no AI agent owns
    // the lead: nothing else will ever move it, so the column is a dead end.
    // With an agent the old rule stands, since the agent would override the move.
    const aiOwnsDraggedLead = Boolean(active.data.current?.assignedAgentId);
    const sourceLocked = !MOVABLE_STAGES.has(sourceMeta.stage) && aiOwnsDraggedLead;
    if (sourceLocked || !MOVABLE_STAGES.has(targetMeta.stage)) {
      toast.error("No puedes mover leads desde o hacia las columnas de WhatsApp.");
      return;
    }

    // Belt to the droppable={} braces below: a fianza column is not a drop target for a
    // merchant's own advisors, and the endpoint rejects the move either way.
    if (!canMoveLeadToPipelinePosition({ email, pipelinePositionId: targetPositionId })) {
      toast.error("No autorizado");
      return;
    }

    const sourceKey = buildColumnQueryInput(merchantId, pipelineId, sourcePositionId, filters);
    const targetKey = buildColumnQueryInput(merchantId, pipelineId, targetPositionId, filters);

    const prevSource = utils.leads.listActiveByPosition.getInfiniteData(sourceKey);
    const prevTarget = utils.leads.listActiveByPosition.getInfiniteData(targetKey);

    const movedLead = prevSource?.pages.flatMap((p) => p.data).find((l) => l.id === leadId);
    if (!movedLead) return;

    // Optimistic: remove from source pages, prepend to first target page.
    if (prevSource) {
      utils.leads.listActiveByPosition.setInfiniteData(sourceKey, {
        ...prevSource,
        pages: prevSource.pages.map((page) => ({
          ...page,
          data: page.data.filter((l) => l.id !== leadId),
        })),
      });
    }

    if (prevTarget) {
      const updatedLead = { ...movedLead, pipelinePositionId: targetPositionId };
      const [firstPage, ...restPages] = prevTarget.pages;
      const newPages = firstPage
        ? [{ ...firstPage, data: [updatedLead, ...firstPage.data] }, ...restPages]
        : prevTarget.pages;
      utils.leads.listActiveByPosition.setInfiniteData(targetKey, {
        ...prevTarget,
        pages: newPages,
      });
    } else {
      utils.leads.listActiveByPosition.invalidate(targetKey);
    }

    try {
      await moveMutation.mutateAsync({
        merchantId,
        leadId,
        pipelinePositionId: targetPositionId,
      });
      toast.success(`Lead movido a ${targetMeta.displayName}`);
      posthog.capture("tablero lead moved", {
        merchant_id: merchantId,
        lead_id: leadId,
        pipeline_id: pipelineId,
        source_position_id: sourcePositionId,
        source_position_name: sourceMeta.displayName,
        source_stage: sourceMeta.stage,
        target_position_id: targetPositionId,
        target_position_name: targetMeta.displayName,
        target_stage: targetMeta.stage,
      });
    } catch (error) {
      // Roll back both columns on failure.
      if (prevSource) {
        utils.leads.listActiveByPosition.setInfiniteData(sourceKey, prevSource);
      }
      if (prevTarget) {
        utils.leads.listActiveByPosition.setInfiniteData(targetKey, prevTarget);
      }
      toast.error("No pudimos mover el lead. Intenta de nuevo.");
      console.error("movePipelinePosition failed", error);
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <StickyScrollRail className={cn("flex h-full gap-3 p-4", allEmpty && "hidden")}>
        {positions.map((position) => {
          const movable = MOVABLE_STAGES.has(position.stage);
          // Leads may always leave a fianza column, so only the drop side is gated.
          const droppable =
            movable && canMoveLeadToPipelinePosition({ email, pipelinePositionId: position.id });
          return (
            <PositionColumn
              key={position.id}
              merchantId={merchantId}
              pipelineId={pipelineId}
              position={position}
              filters={filters}
              mode="kanban"
              draggable={movable}
              droppable={droppable}
              onSelectLead={onSelectLead}
              selectedLeadId={selectedLeadId}
              employeeNameById={employeeNameById}
              onLeadsChange={handleLeadsChange}
            />
          );
        })}
      </StickyScrollRail>
      {allEmpty ? (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm text-neutral-600">
            Ningún lead coincide con los filtros aplicados
          </p>
          {anyFilter ? <Button onClick={onClearFilters}>Limpiar filtros</Button> : null}
        </div>
      ) : null}
    </DndContext>
  );
};

export default KanbanBoard;

// Phone-search mode: one query already resolved by the container, grouped
// client-side by position. Drag is disabled — patching one flat result set is
// a different cache path from the per-column infinite queries, so this stays
// read-only.
// Search results, grouped into the board's columns.
//
// `nonRealtimeIds` marks the rows that came from Tinybird's name match rather
// than DynamoDB. A single divider cannot work here the way it does in the lista
// view — the two groups are interleaved across every column — so the rows carry
// the disclosure themselves, and each column orders its realtime rows first.
export const PhoneColumns = ({
  positions,
  leads,
  nonRealtimeIds,
  isLoading,
  onSelectLead,
  selectedLeadId,
  employeeNameById,
}: {
  positions: PipelinePosition[];
  leads: TableroCardLead[];
  nonRealtimeIds?: Set<string>;
  isLoading: boolean;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
  employeeNameById: Map<string, string>;
}) => {
  const grouped = useMemo(() => {
    const map = new Map<string, TableroCardLead[]>();
    for (const lead of leads) {
      const list = map.get(lead.pipelinePositionId) ?? [];
      list.push(lead);
      map.set(lead.pipelinePositionId, list);
    }
    for (const list of map.values()) {
      list.sort(
        (a, b) =>
          Number(!!nonRealtimeIds?.has(a.id)) - Number(!!nonRealtimeIds?.has(b.id)) ||
          (b.lastLeadMessageAt ?? "").localeCompare(a.lastLeadMessageAt ?? "")
      );
    }
    return map;
  }, [leads, nonRealtimeIds]);

  return (
    <StickyScrollRail className="flex h-full gap-3 p-4">
      {positions.map((position) => {
        const positionLeads = grouped.get(position.id) ?? [];
        return (
          <Columna
            key={position.id}
            title={position.displayName}
            accent={headerColorForStage(position.stage)}
            countLabel={`${positionLeads.length}`}
            droppableId={`position:${position.id}`}
            droppableDisabled
            isLoading={isLoading}
            itemIds={positionLeads.map((l) => l.id)}
            emptyText="Sin coincidencias"
          >
            {positionLeads.map((lead) => (
              <div key={lead.id}>
                <ProspectoCard
                  lead={lead}
                  selected={selectedLeadId === lead.id}
                  draggable={false}
                  onClick={() => onSelectLead(lead.id)}
                  employeeNameById={employeeNameById}
                />
                {nonRealtimeIds?.has(lead.id) && (
                  <span className="mt-1 block px-1 text-[11px] text-neutral-500">
                    Coincidencia por nombre · datos no en tiempo real
                  </span>
                )}
              </div>
            ))}
          </Columna>
        );
      })}
    </StickyScrollRail>
  );
};
